"use client";

import { auth, isFirebaseInitialized } from "@/lib/firebase";
import { getAmplitudeDeviceIdForHeader } from "@/lib/analytics/amplitude";
import { captureAuthFailureTelemetry } from "@/lib/auth-failure-telemetry";

const EXPIRED_TOKEN_ERROR_CODES = new Set([
  "auth/id-token-expired",
  "auth/user-token-expired",
  "auth/session-cookie-expired",
  "auth/id-token-revoked",
]);
const EXPIRED_TOKEN_ERROR_HINTS = ["expired", "revoked"];

function extractFirebaseErrorCode(error: unknown): string | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as { code?: unknown }).code === "string"
  ) {
    return (error as { code?: string }).code;
  }
  return undefined;
}

function isExpiredTokenCode(code?: string | null): boolean {
  if (!code) {
    return false;
  }

  const normalizedCode = code.toLowerCase();
  if (EXPIRED_TOKEN_ERROR_CODES.has(normalizedCode)) {
    return true;
  }

  return EXPIRED_TOKEN_ERROR_HINTS.some((hint) =>
    normalizedCode.includes(hint)
  );
}

export class TokenRefreshError extends Error {
  constructor(message: string, public readonly code?: string) {
    super(message);
    this.name = "TokenRefreshError";
  }

  get isExpired(): boolean {
    return isExpiredTokenCode(this.code);
  }
}

/**
 * Centralized token management service
 * Handles Firebase token retrieval and automatic refresh
 */
export class TokenService {
  private static instance: TokenService;
  private refreshPromise: Promise<string> | null = null;
  private lastToken: string | null = null;
  private lastTokenUserId: string | null = null;
  private tokenExpiry: number | null = null;

  private constructor() {}

  /**
   * Get singleton instance
   */
  public static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }

  /**
   * Get valid authentication token
   * Automatically refreshes if expired or about to expire
   */
  public async getValidToken(): Promise<string> {
    if (!isFirebaseInitialized() || !auth) {
      const error = new Error("Firebase not initialized");
      captureAuthFailureTelemetry({
        authFlow: "session_restore",
        authMethod: "unknown",
        authStage: "token_refresh",
        error,
        failureCategory: "unknown",
        requestTarget: "token",
      });
      throw error;
    }
    const user = auth.currentUser;
    if (!user) {
      const error = new Error("User not authenticated");
      captureAuthFailureTelemetry({
        authFlow: "session_restore",
        authMethod: "unknown",
        authStage: "token_refresh",
        error,
        failureCategory: "unknown",
        requestTarget: "token",
      });
      throw error;
    }

    // Check if we have a valid cached token
    if (
      this.lastToken &&
      this.lastTokenUserId === user.uid &&
      this.tokenExpiry &&
      Date.now() < this.tokenExpiry - 60000
    ) {
      return this.lastToken;
    }

    // If there's already a refresh in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start new refresh
    this.refreshPromise = this.refreshToken();

    try {
      const token = await this.refreshPromise;
      return token;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Force refresh the token
   */
  public async refreshToken(): Promise<string> {
    if (!isFirebaseInitialized() || !auth) {
      const error = new Error("Firebase not initialized");
      captureAuthFailureTelemetry({
        authFlow: "token_refresh",
        authMethod: "unknown",
        authStage: "token_refresh",
        error,
        failureCategory: "unknown",
        requestTarget: "token",
      });
      throw error;
    }
    const user = auth.currentUser;
    if (!user) {
      const error = new Error("User not authenticated");
      captureAuthFailureTelemetry({
        authFlow: "token_refresh",
        authMethod: "unknown",
        authStage: "token_refresh",
        error,
        failureCategory: "unknown",
        requestTarget: "token",
      });
      throw error;
    }

    try {
      // Force refresh by passing true to getIdToken
      const token = await user.getIdToken(true);

      // Cache the token and its expiry
      this.lastToken = token;
      this.lastTokenUserId = user.uid;

      // Decode token to get expiry time
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        this.tokenExpiry = payload.exp * 1000; // Convert to milliseconds
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        // If we can't decode, assume token expires in 1 hour
        this.tokenExpiry = Date.now() + 3600000;
      }

      return token;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      captureAuthFailureTelemetry({
        authFlow: "token_refresh",
        authMethod: "unknown",
        authStage: "token_refresh",
        error,
        requestTarget: "token",
      });
      if (error instanceof TokenRefreshError) {
        throw error;
      }
      const code = extractFirebaseErrorCode(error);
      const message = isExpiredTokenCode(code)
        ? "Token has expired"
        : error instanceof Error
          ? error.message
          : "Token refresh failed";
      throw new TokenRefreshError(message, code);
    }
  }

  /**
   * Get authentication headers for API requests
   */
  public async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const token = await this.getValidToken();
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      const amplitudeDeviceId = getAmplitudeDeviceIdForHeader();
      if (amplitudeDeviceId) {
        headers["x-amplitude-device-id"] = amplitudeDeviceId;
      }
      return headers;
    } catch (error) {
      console.error("Failed to get auth headers:", error);
      captureAuthFailureTelemetry({
        authFlow: "authenticated_api",
        authMethod: "unknown",
        authStage: "auth_header_build",
        error,
        failureCategory: "unknown",
      });
      return {};
    }
  }

  /**
   * Clear cached token (useful for logout)
   */
  public clearToken(): void {
    this.lastToken = null;
    this.lastTokenUserId = null;
    this.tokenExpiry = null;
    this.refreshPromise = null;
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return (
      isFirebaseInitialized() && auth !== null && auth.currentUser !== null
    );
  }
}

// Export singleton instance
export const tokenService = TokenService.getInstance();
